import { RevelioPageId } from '../../components/PageConfigProvider/types';
import {
  type DateRangeFilter,
  type Filter,
  FilterId,
  FilterType,
  type ListFilter,
  type PrimaryFilterId,
  type RangeFilter,
  type SegmentedFilter,
  type StaticListFilter,
  type TreeFilter,
} from '../types/filters';

const DISCOVERY_PAGE_IDS: ReadonlySet<RevelioPageId> = new Set([
  RevelioPageId.TALENT_DISCOVERY,
  RevelioPageId.POSTING_DISCOVERY,
  RevelioPageId.COMPANY_SCREENER_V2,
  RevelioPageId.GEOGRAPHY_SCREENER_V2,
  RevelioPageId.ROLE_SCREENER_V2,
]);

export const isDiscoveryPage = (pageId: RevelioPageId | undefined): boolean =>
  pageId !== undefined && DISCOVERY_PAGE_IDS.has(pageId);

export const isPrimaryFilterId = (
  id: string | undefined
): id is PrimaryFilterId => {
  return (
    id === FilterId.COMPANY || id === FilterId.GEOGRAPHY || id === FilterId.ROLE
  );
};

/** ======== Has Primary Filter ======== */
export const hasPrimaryFilter = ({
  primaryDimension,
  filters,
}: {
  primaryDimension: PrimaryFilterId;
  filters: Filter[];
}): boolean => {
  if (primaryDimension === FilterId.COMPANY) {
    return filters.some((filter) => filter.id === FilterId.COMPANY);
  }

  if (primaryDimension === FilterId.GEOGRAPHY) {
    return filters.some((filter) => filter.id === FilterId.GEOGRAPHY);
  }

  if (primaryDimension === FilterId.ROLE) {
    return filters.some((filter) => filter.id === FilterId.ROLE);
  }

  return false;
};

export const isDateRangeFilter = (
  filter: Filter
): filter is DateRangeFilter => {
  return filter?.type === FilterType.DATE_RANGE;
};

export const isTreeFilter = (filter: Filter): filter is TreeFilter => {
  return filter?.type === FilterType.TREE;
};

/**
 * Generic helper to create a SegmentedFilter for a specific wrapped filter type.
 */
const createSegmentedFilterForType = <
  T extends TreeFilter | ListFilter | StaticListFilter | RangeFilter,
>(
  wrappedFilter: T,
  baseProps: Filter,
  segmentations: Record<string, Filter>
): SegmentedFilter<T> => {
  return {
    ...baseProps,
    type: FilterType.SEGMENTED,
    wrappedFilter,
    segmentations,
  } as SegmentedFilter<T>;
};

/**
 * Type-safe utility to create a SegmentedFilter from a wrapped filter.
 * Handles all segmentable filter types (TREE, LIST, STATIC_LIST, RANGE).
 */
export const createSegmentedFilter = ({
  wrappedFilter,
  baseProps,
  segmentations,
}: {
  wrappedFilter: TreeFilter | ListFilter | StaticListFilter | RangeFilter;
  baseProps: Filter;
  segmentations: Record<string, Filter>;
}): Filter => {
  const { type } = wrappedFilter;

  if (type === FilterType.TREE) {
    return createSegmentedFilterForType<TreeFilter>(
      wrappedFilter,
      baseProps,
      segmentations
    );
  }
  if (type === FilterType.LIST) {
    return createSegmentedFilterForType<ListFilter>(
      wrappedFilter,
      baseProps,
      segmentations
    );
  }
  if (type === FilterType.STATIC_LIST) {
    return createSegmentedFilterForType<StaticListFilter>(
      wrappedFilter,
      baseProps,
      segmentations
    );
  }
  // RANGE
  return createSegmentedFilterForType<RangeFilter>(
    wrappedFilter,
    baseProps,
    segmentations
  );
};

/** ======== Range Filter Helpers ======== */

const DEFAULT_MIN_YEAR = 1000;
const DEFAULT_MAX_YEAR = 9999;

export const isFundingFilter = (filterId: FilterId): boolean => {
  return (
    filterId === FilterId.LAST_FUNDING_AMOUNT ||
    filterId === FilterId.TOTAL_FUNDING_AMOUNT ||
    filterId === FilterId.VALUATION
  );
};

export const isYearFilter = (filterId: FilterId): boolean => {
  return (
    filterId === FilterId.GRADUATION_YEAR ||
    filterId === FilterId.LAST_FUNDING_YEAR ||
    filterId === FilterId.YEAR_FOUNDED
  );
};

export const isMinEffectivelySet = (
  min: number | undefined,
  filterId: FilterId,
  isPayFilter: boolean,
  isFundingFilter: boolean
): boolean => {
  if (min === undefined) return false;

  if (isPayFilter && min <= 0) return false;
  if (isFundingFilter && min <= 0) return false;

  if (isYearFilter(filterId) && min === DEFAULT_MIN_YEAR) {
    return false;
  }

  return true;
};

export const isMaxEffectivelySet = (
  max: number | undefined,
  filterId: FilterId,
  isMinSet: boolean
): boolean => {
  if (max === undefined) return false;

  if (isFundingFilter(filterId) && max === Number.MAX_SAFE_INTEGER) {
    return false;
  }

  if (isYearFilter(filterId)) {
    if (max === DEFAULT_MAX_YEAR) return false;
    const currentYear = new Date().getFullYear();
    if (max === currentYear && isMinSet) return false;
  }

  return true;
};
